import {
  Box,
  Button,
  Card,
  CardBody,
  Flex,
  FormControl,
  FormErrorMessage,
  Icon,
  Text,
} from '@chakra-ui/react';
import { userManageAction } from 'actions';
import { PageContainer, PageHeader } from 'components/ds';
import { Field, FieldProps, Form, Formik } from 'formik';
import { Save, ShieldCheck } from 'lucide-react';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import PasswordField from './password-field';

const ChangePasswordForm = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const { loadingBtnChangePw } = useSelector((state) => state.userManage);

  const [showOldPw, setShowOldPw] = useState(false);
  const [showNewPw, setShowNewPw] = useState(false);
  const [showConfirmNewPw, setShowConfirmNewPw] = useState(false);

  const validateOldPassword = useCallback(
    (value: string) => {
      let error;
      if ((value || '').trim().length === 0) {
        error = t('changePassword.oldPasswordRequired');
      }
      return error;
    },
    [i18n.language, t]
  );

  const validateNewPassword = useCallback(
    (value: string) => {
      let error;
      if ((value || '').trim().length === 0) {
        error = t('changePassword.newPasswordRequired');
      }
      return error;
    },
    [i18n.language, t]
  );

  const validateConfirmPassword = useCallback(
    (newPassword: string, value: string) => {
      let textErr;
      if (value !== newPassword) {
        textErr = t('changePassword.newPasswordNotMatch');
      }
      return textErr;
    },
    [i18n.language, t]
  );

  return (
    <PageContainer>
      {/* Page header — shared DS component, left-aligned at page edge (mockup: .page-header) */}
      <PageHeader title={t('changePassword.changePassword')} />

      {/* Security card (mockup: ShieldCheck + "Security" section) — readable max-width inside page */}
      <Card
        maxW='640px'
        bg='bg.surface'
        borderWidth='1px'
        borderColor='border.default'
        borderRadius='lg'
        shadow='ds_sm'
        variant='unstyled'
      >
        <CardBody p={6}>
          <Flex align='center' gap={2} mb={4}>
            <Icon as={ShieldCheck} boxSize='18px' color='primary.solid' />
            <Text color='text.default' fontFamily='heading' fontSize='md' fontWeight={600}>
              {t('changePassword.changePassword')}
            </Text>
          </Flex>

          <Formik
            i18nIsDynamicList
            initialValues={{
              confirmNewPwd: '',
              oldPassword: '',
              newPassword: '',
            }}
            onSubmit={(values, formProps) => {
              dispatch(userManageAction.changePassword(values));
              formProps.resetForm();
            }}
          >
            {({ errors, touched, values, resetForm }) => {
              i18n.on('languageChanged', (lng) => {
                resetForm();
              });
              return (
                <>
                  <Form id='formFormikPw'>
                    <Field name='oldPassword' validate={validateOldPassword}>
                      {({ field }: FieldProps) => (
                        <FormControl mb={4} isInvalid={!!errors.oldPassword && touched.oldPassword}>
                          <PasswordField
                            field={field}
                            id='oldPassword'
                            label={t('changePassword.oldPassword')}
                            show={showOldPw}
                            onToggle={() => setShowOldPw(!showOldPw)}
                            toggleLabel={t('changePassword.oldPassword')}
                          />
                          <FormErrorMessage color='state.error' fontSize='xs'>
                            {errors.oldPassword}
                          </FormErrorMessage>
                        </FormControl>
                      )}
                    </Field>
                    <Field id='newPassword' name='newPassword' validate={validateNewPassword}>
                      {({ field }: FieldProps) => (
                        <FormControl mb={4} isInvalid={!!errors.newPassword && touched.newPassword}>
                          <PasswordField
                            field={field}
                            id='newPassword'
                            label={t('changePassword.newPassword')}
                            show={showNewPw}
                            onToggle={() => setShowNewPw(!showNewPw)}
                            toggleLabel={t('changePassword.newPassword')}
                          />
                          <FormErrorMessage color='state.error' fontSize='xs'>
                            {errors.newPassword}
                          </FormErrorMessage>
                        </FormControl>
                      )}
                    </Field>
                    <Field
                      name='confirmNewPwd'
                      validate={(value: string) =>
                        validateConfirmPassword(values.newPassword, value)
                      }
                    >
                      {({ field }: FieldProps) => (
                        <FormControl
                          mb={6}
                          isInvalid={!!(errors.confirmNewPwd && touched.confirmNewPwd)}
                        >
                          <PasswordField
                            field={field}
                            id='confirmNewPwd'
                            label={t('changePassword.confirmNewPassword')}
                            show={showConfirmNewPw}
                            onToggle={() => setShowConfirmNewPw(!showConfirmNewPw)}
                            toggleLabel={t('changePassword.confirmNewPassword')}
                          />
                          <FormErrorMessage color='state.error' fontSize='xs'>
                            {errors.confirmNewPwd}
                          </FormErrorMessage>
                        </FormControl>
                      )}
                    </Field>
                  </Form>
                  <Box>
                    <Button
                      form='formFormikPw'
                      isDisabled={
                        !values.newPassword ||
                        !values.oldPassword ||
                        !!errors.newPassword ||
                        !!errors.oldPassword ||
                        !!errors.confirmNewPwd
                      }
                      isLoading={loadingBtnChangePw}
                      leftIcon={<Icon as={Save} boxSize='16px' />}
                      loadingText={t('changePassword.changing')}
                      size='ds-md'
                      type='submit'
                      variant='ds-soft'
                    >
                      {t('common.change')}
                    </Button>
                  </Box>
                </>
              );
            }}
          </Formik>
        </CardBody>
      </Card>
    </PageContainer>
  );
};
export default ChangePasswordForm;
